import { FC, useMemo } from "react";

import { Theme, SxProps, useTheme, Box } from "@mui/material";
import { getYoutubeIdentifier } from "@simplyk/common";

import { useTranslate } from "@/common/hooks/useTranslate";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import Image from "@/components/Image/Image";

type MobileBannerSharedProps = {
  id?: string;
  "data-test"?: string;
  sx?: SxProps<Theme>;
  useSquareRatioOnMobile?: boolean;
};

export type MobileCampaignBannerViewProps = MobileBannerSharedProps &
  (
    | { imageSrc: string; imageAlt?: string | null; videoSrc: undefined; onLoadingComplete?: () => void }
    | { imageSrc: undefined; videoSrc: string }
  );

export const MobileCampaignBannerView: FC<MobileCampaignBannerViewProps> = (props) => {
  const { imageSrc, videoSrc, ...rest } = props;

  if (videoSrc) {
    return <MobileVideoBanner src={videoSrc} {...rest} />;
  }

  if (imageSrc) {
    const { imageAlt, ...imageRest } = rest as { imageAlt?: string | null } & MobileBannerSharedProps & {
        onLoadingComplete?: () => void;
      };
    return <MobileImageBanner src={imageSrc} alt={imageAlt} {...imageRest} />;
  }

  // Should never happen due to TypeScript's exhaustive checking
  return null;
};

type MobileVideoBannerProps = MobileBannerSharedProps & {
  src: string;
  useSquareRatioOnMobile?: boolean;
};

const MobileVideoBanner: FC<MobileVideoBannerProps> = ({ src, sx, useSquareRatioOnMobile, ...rest }) => {
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();
  const youtubeIdentifier = useMemo(() => getYoutubeIdentifier(src), [src]);

  if (!isSmallScreen) {
    return null;
  }

  const styles = {
    width: "100%",
    maxHeight: useSquareRatioOnMobile ? "none" : "300px",
    aspectRatio: useSquareRatioOnMobile ? "1 / 1" : "16 / 9",
    lineHeight: 0,
    borderRadius: 2,
    ...(sx as object), // StyledIFrame does not support the full range of sx props
  };

  return (
    <Box sx={{ paddingInline: 2 }}>
      <Box
        component="iframe"
        title={t("common", "a11y.campaignVideo")}
        src={`https://www.youtube.com/embed/${youtubeIdentifier}?autoplay=1&mute=1&loop=1;showinfo=0`}
        allowFullScreen
        frameBorder="0"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture;"
        data-chromatic="ignore"
        sx={styles}
        {...rest}
      />
    </Box>
  );
};

type MobileImageBannerProps = MobileBannerSharedProps & {
  src: string;
  alt?: string | null;
  onLoadingComplete?: () => void;
  useSquareRatioOnMobile?: boolean;
};

const MobileImageBanner: FC<MobileImageBannerProps> = ({
  src,
  alt,
  onLoadingComplete,
  sx,
  useSquareRatioOnMobile,
  ...rest
}) => {
  const theme = useTheme();
  const { isSmallScreen } = useMediaQuery();

  if (!isSmallScreen) {
    return null;
  }

  const styles = {
    maxHeight: useSquareRatioOnMobile ? "none" : "300px",
    backgroundColor: theme.palette.forms.surface.quiet,
    aspectRatio: useSquareRatioOnMobile ? "1 / 1" : "16 / 9",
    objectFit: "cover",
    width: "100%",
    height: "auto",
    borderRadius: theme.spacing(2),
    ...(sx as object), // Image.tsx does not support the full range of sx props
  };

  return (
    <Box sx={{ paddingInline: 2 }}>
      <Image
        src={src}
        alt={alt ?? ""}
        resolution={1200}
        priority
        nextImage
        onLoadingComplete={onLoadingComplete}
        data-test="banner-image"
        width={585}
        height={useSquareRatioOnMobile ? 585 : 330}
        sx={styles}
        {...rest}
      />
    </Box>
  );
};
